import OnBoardingLayout from "../OnBoardingLayout.jsx";
import FormContainer from "../../../../components/form/FormContainer.jsx";
import FormControl from "../../../../components/form/FormControl.jsx";
import {useContext, useState} from "react";
import {useTranslation} from "react-i18next";
import FormButton from "../../../../components/form/FormButton.jsx";
import useDownloadState from "../../../../api/utilities/useDownloadState.js";
import FormError from "../../../../components/form/FormError.jsx";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import {ProjectApi, UserApi} from "../../../../api/authority/generated/index.ts";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import Button from "../../../../components/Button.jsx";
import {useNavigate} from "react-router-dom";

export default function AccountNameStep() {

    let {t} = useTranslation();
    let navigate = useNavigate();

    let {
        context,
        getAuthorityConfiguration,
        resetCurrentUserProjects,
        setCurrentProjectId,
        updateIdentityUser
    } = useContext(GlobalContext);

    let billingAccount = null;
    for (let project of context.currentUserProjects) {
        if (project.billingAccount) {
            billingAccount = project.billingAccount;
        }
    }

    /** @var {BillingAccountInput} billingAccountInput */
    let [billingAccountInput, setBillingAccountInput] = useState({
        firstName: context.currentIdentity?.user?.firstName ?? '',
        lastName: context.currentIdentity?.user?.lastName ?? '',
        phone: context.currentIdentity?.user?.phone ?? '',
    });

    let userId = context.currentIdentity?.user?.id;

    let [formError, setFormError] = useState(null);

    // download states
    let [creatingState, setCreatingDownloading, setCreatingResult, setCreatingError] = useDownloadState();
    let [updatingState, setUpdatingDownloading, setUpdatingResult, setUpdatingError] = useDownloadState();

    // Compute first validation error or null
    const getFirstError = (data) => {
        const firstName = data.firstName.trim();
        const lastName = data.lastName.trim();
        const phoneNoSpaces = data.phone.replace(/\s+/g, '');

        if (firstName === '') return t('validation.first-name-required');
        if (lastName === '') return t('validation.last-name-required');
        if (phoneNoSpaces.length < 9) return t('validation.phone-min-length');
        if (phoneNoSpaces.length > 16) return t('validation.phone-max-length');
        return null;
    };

    // Submit handler performing validation + API call
    const handleSubmit = async () => {
        const err = getFirstError(billingAccountInput);
        if (err) {
            setFormError(err);
            return;
        }
        setFormError(null);

        // start loading
        setCreatingDownloading(true);
        try {
            const api = new UserApi(getAuthorityConfiguration());
            const res = await api.updateProfile({
                userId: userId,
                userProfileInput: {
                    firstName: billingAccountInput.firstName.trim(),
                    lastName: billingAccountInput.lastName.trim(),
                    // send phone without spaces
                    phone: billingAccountInput.phone.replace(/\s+/g, ''),
                }
            });
            updateIdentityUser(res);
            navigate('/');
        } catch (e) {
            // show generic error
            setCreatingError(e);
        } finally {
            // always stop loader
            setCreatingDownloading(false);
        }
    };

    // First name handler (clears visible error while typing)
    const handleFirstNameChange = (e) => {
        setBillingAccountInput(prev => ({ ...prev, firstName: e.target.value }));
        if (formError) setFormError(null);
    };

    // Last name handler
    const handleLastNameChange = (e) => {
        setBillingAccountInput(prev => ({ ...prev, lastName: e.target.value }));
        if (formError) setFormError(null);
    };

    // Phone handler: keep digits, spaces, allow single leading +; cap 16 non-space chars
    const handlePhoneChange = (e) => {
        const raw = e.target.value;

        // keep digits, spaces, plus
        let filtered = raw.replace(/[^\d+ ]/g, '');

        // allow single '+' only at the start
        if (filtered.startsWith('+')) {
            filtered = '+' + filtered.slice(1).replace(/\+/g, '');
        } else {
            filtered = filtered.replace(/\+/g, '');
        }

        // enforce max 16 non-space characters
        const nonSpace = filtered.replace(/\s+/g, '');
        if (nonSpace.length > 16) return;

        setBillingAccountInput(prev => ({ ...prev, phone: filtered }));
        if (formError) setFormError(null);
    };

    return (
        <OnBoardingLayout>
            <FormContainer>
                <LoadingComponent loading={creatingState.downloading || updatingState.downloading}>
                    {creatingState.error && <FormError error={creatingState.error}/>}
                    {formError && <FormError error={formError}/>}
                    <FormControl
                        label={t('user.first-name')}
                        value={billingAccountInput.firstName}
                        onChange={handleFirstNameChange}
                        variant={'onboarding'}
                    />
                    <FormControl
                        className={'mt-3'}
                        label={t('user.last-name')}
                        value={billingAccountInput.lastName}
                        onChange={handleLastNameChange}
                        variant={'onboarding'}
                    />
                    <FormControl
                        className={'mt-3'}
                        label={t('user.phone')}
                        value={billingAccountInput.phone}
                        onChange={handlePhoneChange}
                        variant={'onboarding'}
                    />
                    <FormButton
                        className={'button-primary mt-2'}
                        onClick={handleSubmit}
                    >
                        {t('onboarding.next-step')}
                    </FormButton>
                </LoadingComponent>
            </FormContainer>
        </OnBoardingLayout>
    );
}
